<script lang="ts">
  import { slide } from "svelte/transition";
  import Controller from "./Controller.svelte";
  import { get, writable, type Writable } from "svelte/store";
  import { AlternativePriceDisplay, type Row } from "@/App.svelte";
  import { convertStaticResponseToTable } from "@/App.svelte";
  import { type Header } from "@/lib/DataTable.svelte";
  import Button from "@/lib/Button.svelte";
  import { createEventDispatcher, getContext, tick } from "svelte";
  import DataTableV2 from "./DataTableV2.svelte";
  import { getTableCategoryGroup, getFacetParams } from "@/lib/api/category";

  export let headers: Header<Row>[];
  export let rows: Writable<Row[]>;
  export let tableLength: number;
  export let index: number;
  export let groupName: string | null;
  export let categoryId: string;

  let activeHeaderParam: Writable<{ orderBy: string; direction: string } | null> = writable(null);
  let descDataUrl = `data:image/svg+xml,${encodeURIComponent(`<svg width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M3.5 7 7 0H0l3.5 7z" fill="#000"/>
</svg>`)}`;
  let ascDataUrl = `data:image/svg+xml,${encodeURIComponent(`<svg width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M3.5 0 7 7H0l3.5-7z" fill="#000"/>
</svg>`)}`;
  let suspended = false;
  let isCompact = true;
  let instance: Controller<Row>;
  let expandedId: string | null = null;
  let visibleRows: Writable<Row[]> = writable([]);
  let tablePageNumber = 1;
  let columnsGTC: string = `70px 60px 140px repeat(${headers.length - 4}, minmax(min-content, 1fr)) 90px`;
  let dollarFormat = new Intl.NumberFormat("en", { style: "currency", currency: "USD" });
  let sortFetchFlag = false;
  let tableSuspended = false;

  const dispatch = createEventDispatcher();

  async function handleRowClick(row: Row, e: Event) {
    const partNumber = row.productId;

    if (e.target instanceof HTMLElement && !e.target.closest(".expanded-row-detail")) {
      if (expandedId === row.id) {
        window.digitalData.event.push({
          name: "product_list_page",
          data: {
            methodDetails: "product hide",
            methodName: "product list page:table view",
            groupName: groupName, //ex:Aluminum,
            partNumber: partNumber.startsWith("P") ? partNumber.slice(1) : partNumber, //ex:750027
          },
        });

        expandedId = null;
      } else {
        expandedId = row.id;
        if (expandedId !== null) {
          window.digitalData.event.push({
            name: "product_list_page",
            data: {
              methodDetails: "product expand",
              methodName: "product list page:table view",
              groupName: groupName, //ex:Aluminum,
              partNumber: partNumber.startsWith("P") ? partNumber.slice(1) : partNumber, //ex:750027
            },
          });
          dispatch("rowExpanded", { id: row.id, index, partNumber });
        }
      }
    }
  }

  async function handleHeaderClick(x: any) {
    if (!x.detail.sort) return;
    window.digitalData.event.push({
      name: "product_list_page",
      data: {
        methodDetails: `Table View|${groupName}|${x.detail.display}`, //ex:Table View|Aluminum|MFNR
        methodName: "product list page :table view:sorting",
        groupName: groupName, //ex:Aluminum
      },
    });

    updateHeaderState();
    tablePageNumber = 1;

    sortFetchFlag = true;
    tableSuspended = true;
    expandedId = null;

    await instance.triggerGetData({}, true);
    sortFetchFlag = false;
    tableSuspended = false;

    //-- unfortunate hack to get transitions to behave
    expandedId = "";
    await tick();
    expandedId = null;

    //----
    function updateHeaderState() {
      const key = x.detail.key;
      const updatedHeaders = headers.map((header) => ({
        ...header,
        active: header.key === key,
      }));
      let direction;

      if ($activeHeaderParam) {
        const isCurrentParam = key === $activeHeaderParam.orderBy;
        direction = isCurrentParam ? ($activeHeaderParam.direction === "asc" ? "desc" : "asc") : "asc";
      } else {
        direction = "asc";
      }

      headers = updatedHeaders;
      $activeHeaderParam = {
        orderBy: key,
        direction,
      };
    }
  }

  const getTableContentFn = async (): Promise<{ content: Row[] }> => {
    const PAGE_SIZE = 24;

    // if we're sorting ignore existing rows since they will be replaced
    const existingRowsCount = sortFetchFlag ? 0 : $rows.length;

    // calculate effective page number based on existing rows
    const effectivePageNumber = Math.floor(existingRowsCount / PAGE_SIZE) + 1;

    // skip this during sorting since we want to start fresh
    // if we're on page 1 and have a full page or more of rows (effectivePageNumber > 1)
    if (!sortFetchFlag && tablePageNumber === 1 && effectivePageNumber > 1) {
      tablePageNumber = effectivePageNumber;
    }

    // check if were over the total length
    if (PAGE_SIZE * (tablePageNumber - 1) >= tableLength) return { content: [] };

    let currentBeginIndex: number | undefined;
    let currentPageSize = PAGE_SIZE;

    // only apply beginIndex logic if we're not sorting and only on the "current" page (where our existing rows end)
    if (!sortFetchFlag && tablePageNumber === effectivePageNumber) {
      currentBeginIndex = existingRowsCount;

      // allows us to ask for only the required amount of rows needed to get to the next full page size
      currentPageSize = PAGE_SIZE - (existingRowsCount % PAGE_SIZE);

      // if we need a full page, don't use beginIndex
      if (currentPageSize === PAGE_SIZE) {
        currentBeginIndex = undefined;
      }
    }

    const response = await getTableCategoryGroup({
      id: categoryId,
      orderBy: $activeHeaderParam?.orderBy,
      sortDirection: $activeHeaderParam?.direction,
      groupName: groupName === "Additional Options" ? null : groupName,
      pageNumber: tablePageNumber,
      pageSize: currentPageSize,
      facets: getFacetParams(),
      beginIndex: currentBeginIndex,
    });

    const newTableData = convertStaticResponseToTable(response, headers);
    const content = get(newTableData[0].rows);

    tablePageNumber++;
    return { content };
  };

  let handleToggle = () => {
    window.digitalData.event.push({
      name: "product_list_page",
      data: {
        methodDetails: `${groupName} : view ${isCompact ? "all" : "less"}`, //ex:Aluminum:view all
        methodName: "product list page:table view",
        groupName: groupName, //ex:Aluminum,
        TotalProducts: tableLength, //ex:30
      },
    });
    isCompact = !isCompact;
    if (!isCompact) instance.triggerGetData({}, false);
  };

  let lessThanFifteen: boolean = tableLength < 15;

  $: fixedHeight = !lessThanFifteen && !isCompact;
  $: {
    if (isCompact) {
      visibleRows.set($rows.slice(0, 10));
    } else {
      visibleRows.set($rows);
    }
  }

  $: if ($activeHeaderParam) {
    document.documentElement.style.setProperty(
      "--sort-arrow",
      $activeHeaderParam.direction === "asc" ? `url(${ascDataUrl})` : `url(${descDataUrl})`,
    );
  }
  document.documentElement.style.setProperty("--sort-arrow", `url(${ascDataUrl})`);
  document.documentElement.style.setProperty("--asc-arrow", `url(${ascDataUrl})`);
</script>

<Controller
  bind:headers
  bind:rows={$rows}
  bind:this={instance}
  on:fetchStart={() => (suspended = true)}
  on:fetchComplete={() => (suspended = false)}
  {getTableContentFn}
  skipInit
/>

<div
  class:h-[1009px]={fixedHeight}
  class:h-auto={lessThanFifteen}
  class="w-[calc(100vw-360px)] max-w-[1080px] overflow-x-auto"
>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-img-redundant-alt -->
  <DataTableV2
    bind:headers
    bind:rows={$visibleRows}
    enabled
    partialSkeleton={suspended && !tableSuspended}
    bodySkeleton={tableSuspended}
    rowCount={Math.min(isCompact ? 10 : 24, tableLength)}
    skeletonHeight={"66px"}
    on:click:header={handleHeaderClick}
    on:thresholdOver={() => {
      if (!suspended && !isCompact) instance.triggerGetData({}, false);
    }}
    scrollThresholdOptions={{
      threshold: 20,
      horizontal: false,
    }}
    columns={columnsGTC}
  >
    <svelte:fragment slot="row" let:row>
      <div class="table-body-row cursor-pointer" on:click={(e) => handleRowClick(row, e)}>
        {#each headers as header}
          {@const cell = { column: header.key, value: row[header.key] }}
          <div
            class={expandedId != null && row.id == expandedId
              ? "table-body-row-expanded overflow-hidden py-2"
              : "overflow-hidden py-2"}
            style="text-overflow: ellipsis;"
          >
            {#if cell.column === "image"}
              <div class="flex h-full items-center justify-center">
                <img
                  src={"https://images.hdsupplysolutions.com/image/upload/bo_1px_solid_white/e_make_transparent:2/" +
                    "h_50,w_50,q_auto,fl_lossy,d_no_image.gif,f_auto/" +
                    cell.value}
                  alt="Product Image"
                  class="h-[50px] w-[50px]"
                />
              </div>
            {:else}
              <div class="flex h-full items-center justify-center text-center">
                <div
                  class="text-xs"
                  class:line-clamp-5={cell.value && expandedId !== row.id}
                  class:break-all={cell.column == "catalog_code_sort"}
                  class:break-word={cell.column !== "catalog_code_sort"}
                >
                  {#if cell.column === "price"}
                    {#if cell.value === AlternativePriceDisplay.Unavailable}
                      --
                    {:else if cell.value === AlternativePriceDisplay.Login}
                      <Button
                        theme="text"
                        on:click={() => {
                          window.GlobalLoginJS.InitHTTPSecure("Header_GlobalLogin");
                        }}>Sign In</Button
                      >
                    {:else if cell.value === AlternativePriceDisplay.UnavailableList && row.listPrice}
                      {dollarFormat.format(row.listPrice)}
                    {:else}
                      {typeof cell.value === "number" ? dollarFormat.format(cell.value) : ""}
                    {/if}
                  {:else if cell.column === "productId"}
                    {row.productId.startsWith("P") ? row.productId.slice(1) : cell.value}
                  {:else}
                    {@html cell.value}
                  {/if}
                </div>
              </div>
            {/if}
          </div>
        {/each}
        {#if expandedId === row.id}
          <div class="expanded-row-detail cursor-[initial]" in:slide|local out:slide|local>
            <div class="mimic-list-tile-width sticky left-0 max-w-[1080px]">
              <div class="">
                {#if row.html}
                  <div class="p-4" in:slide|local>
                    {@html row.html}
                  </div>
                {:else}
                  <div
                    out:slide|local={{ duration: 150 }}
                    class="flex h-[254px] items-center justify-center p-4 text-gray-300"
                  >
                    <span>Loading...</span>
                  </div>
                {/if}
              </div>
            </div>
          </div>
        {/if}
      </div>
    </svelte:fragment>
  </DataTableV2>
</div>
{#if tableLength > 10}
  <div class="flex justify-center">
    <Button theme="hollow" class="table-view-button" on:click={() => handleToggle()}>
      <div class="text-[#335cb3]">
        {#if isCompact}
          View all - {tableLength} Results
        {:else}
          View Less
        {/if}
      </div>
    </Button>
  </div>
{:else}
  <div class="h-[84px]" />
{/if}

<style>
  .table-body-row {
    display: contents;
  }

  .line-clamp-5 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
  }

  :global(.table-header) {
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    padding: 7px 8px 7px 8px !important;
    font-weight: bold;
    justify-items: center;
  }

  :global(.table-header-text) {
    text-align: center;
    font-size: 1.2rem !important;
    margin: 0 8.5px;
    transition: transform .2s ease-in-out;
  }

  :global(.table-header.sortable:hover .table-header-text),
  :global(.table-header.active .table-header-text) {
    margin: 0;
    transform: translateX(-2px);
  }

  .table-body-row:nth-child(2n) > div:not(.expanded-row-detail) {
    background-color: rgb(237, 237, 237);
  }

  .table-body-row:hover > div:not(.expanded-row-detail) {
    background-color: #fff4c3;
    transition: background-color 0.2s ease-in-out;
  }

  .table-body-row-expanded {
    background-color: #f1f3f8 !important;
    height: auto;
  }

  .expanded-row-detail {
    grid-column: 1 / -1;
    border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
  }

  :global(.table-header.sortable .table-header-text::after) {
    content: "";
    background-image: var(--asc-arrow);
    background-repeat: no-repeat;
    background-position: center;
    opacity: 0;
    transform: rotate(-90deg);
    transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out, background-image 0.2s ease-in-out;
  }

  :global(.table-header.sortable:hover .table-header-text::after) {
    width: 7px;
    height: 7px;
    background-image: var(--asc-arrow);
    margin-left: 10px;
    opacity: 1;
    transform: rotate(0deg);
    flex-shrink: 0;
  }
  :global(.table-header.active .table-header-text::after) {
    width: 7px;
    height: 7px;
    background-image: var(--sort-arrow);
    margin-left: 10px;
    opacity: 1;
    transform: rotate(0deg);
    flex-shrink: 0;
  }

  :global(.table-header.active:hover .table-header-text::after) {
    width: 7px;
    height: 7px;
    background-image: var(--sort-arrow);
    transform: rotate(180deg);
    flex-shrink: 0;
  }

  :global(.table-header.sortable:hover) {
    border-bottom: solid 2px #0030a0;
    background-color: #ccd6ec;
    transition: background-color 0.2s ease-in-out;
  }
  :global(.table-header.active:hover) {
    border-bottom: solid 2px #0030a0;
  }
  :global(.table-header) {
    word-break: normal;
    z-index: 9;
  }
  :global(.table-view-button) {
    margin: 24px 0 24px 0;
    border: solid 2px #335cb3 !important;
  }

  :global(.table-view-button:hover) {
    border: solid 2px #f1f3f8 !important;
    background-color: #f1f3f8;
  }
  :global(.subcat-list-tile) {
    margin: 0 !important;
    padding: 0 !important;
    border-bottom: none !important;
  }

  :global(.subcat-list-tile > div:first-child) {
    max-width: calc(100vw - 375px);
    justify-content: space-around !important;
  }

  @media (max-width: 1440px) {
    .mimic-list-tile-width {
      max-width: calc(100vw - 375px);
    }
  }

  @media (max-width: 1250px) {
    :global(.subcat-list-tile__image) {
      min-width: 150px !important;
      max-width: 150px !important;
    }
  }
  :global(.subcat-list-tile__part-quantity-availability) {
    min-width: 150px !important;
  }

  :global(.subcat-list-tile__price-and-add-to-cart) {
    min-width: 150px !important;
    max-width: 200px !important;
  }
</style>
